import { FC, PropsWithChildren, ReactElement, useMemo, ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Stack, Typography } from "@mui/material";
import { Lock } from "@mui/icons-material";

import { useAppSelector } from "@/store";
import { roles } from "@/roles";
import { useGetTenantAllowedPagesQuery } from "@/graphql/page-access";

interface Props {
  protection?: string[];
}
const Protection: FC<PropsWithChildren<Props>> = ({
  children,
  protection,
}): ReactElement => {
  const { user } = useAppSelector((state) => state.auth);

  if (user && protection?.includes(user.userRole)) return <>{children}</>;

  return (
    <Stack
      alignItems="center"
      sx={{
        p: 5,
        borderRadius: 1,
        background: (theme) => theme.palette.grey[200],
      }}
      spacing={{ xs: 0.5, sm: 1.5 }}
    >
      <Lock
        fontSize="large"
        sx={{ color: (theme) => theme.palette.secondary.main }}
      />
      <Typography variant="h5" color="primary.main">
        Permission Denied!
      </Typography>
      <Typography variant="body1">
        {`${"You're not authorised to access this page!"}`}
      </Typography>
    </Stack>
  );
};

export default Protection;

export const useRequirePage = (pageKey: string) => {
  const { user } = useAppSelector((state) => state.auth);
  const location = useLocation();
  const role = (user as any)?.userRole;
  const shouldFetchTenantPages = [roles.SPOWNER, roles.SPPARTNER, roles.SPCLIENT, roles.TDCLIENT].includes(role);
  const { data: tenantPagesResp } = useGetTenantAllowedPagesQuery(undefined, { skip: !shouldFetchTenantPages });
  const allowed = useMemo(() => {
    const userPages = (user as any)?.allowedPages as string[] | undefined;
    const pages = userPages ?? tenantPagesResp?.pages;
    try {
      console.log('[RouteGuard] role:', role, 'userPages:', userPages, 'tenantPages:', tenantPagesResp?.pages, 'checking:', pageKey);
    } catch {}
    if ([roles.SPOWNER, roles.SPPARTNER, roles.SPCLIENT].includes(role)) {
      // For SP roles, if pages are not yet loaded, don't redirect (pre-hydration allow)
      if (!pages) return true;
      // Once loaded, enforce explicit allowlist
      return pages.includes(pageKey);
    }
    // TD Client: use userPages with tenant fallback; no default-allow when template exists
    if (role === roles.TDCLIENT) {
      if (!pages) return true; // pre-hydration; App gates on isUserLoaded, so this window is small
      return pages.includes(pageKey);
    }
    // TD Admin and others: transitional behavior
    if (!pages) return true;
    return pages.includes(pageKey);
  }, [user, tenantPagesResp, pageKey]);

  if (!allowed) {
    return <Navigate to={"/"} state={{ from: location }} replace />;
  }
  return null;
};

export const RequirePage: FC<{ pageKey: string; children: ReactNode }> = ({
  pageKey,
  children,
}) => {
  const redirect = useRequirePage(pageKey);
  if (redirect) return redirect;
  return <>{children}</>;
};
